// Кабинет (README §9): полотно без рамок, max-width 720, gap 40.
//   1. шапка — аватар 44, имя, «выйти»;
//   2. лимиты по провайдерам — GET /api/limits (lib/limits);
//   3. «кто тратит сейчас» — GET /api/usage/by-agent, клик → Конструктор
//      в режиме «расход» с этим агентом;
//   4. сервер — GET /api/server: пульсирующая точка, 4 строки,
//      пометка --warn «рестарт убивает сессии».

import { useEffect, useState } from 'react'
import { fmtPctRate, fmtRate, forecastText, isEnough, leftPercent, mskTime, useLimits, windowLeft, windowTitle } from '../../lib/limits'
import { useAppStore } from '../../store/appStore'
import { SideMenu } from '../../ui/SideMenu'
import { describe, http } from '../custom/http'
import type { SectionModule } from '../types'
import './account.css'

interface AgentRow {
  agentId: string
  kind: 'session' | 'subagent' | 'codex' | 'other'
  provider?: string
  sessionId?: string
  title: string
  engine?: string | null
  agentType?: string
  tokensPerMin: number
  share: number
}
interface ByAgent { rows: AgentRow[]; totalTokensPerMin: number }
interface ServerInfo {
  address: string
  liveSessions: number
  ownLiveSessions: number
  maxSessions: number
  memory: { totalMb: number; availableMb: number }
  disk: { totalGb: number; freeGb: number }
  uptimeSec: { panel: number; system: number }
  restartKillsSessions: boolean
}

const POLL_MS = 30_000

function usePolled<T>(path: string): { data: T | null; error: string | null } {
  const [state, setState] = useState<{ data: T | null; error: string | null }>({ data: null, error: null })
  useEffect(() => {
    let alive = true
    const load = async () => {
      try {
        const data = await http<T>(path)
        if (alive) setState({ data, error: null })
      } catch (error) {
        if (alive) setState((s) => ({ data: s.data, error: describe(error) }))
      }
    }
    void load()
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load()
    }, POLL_MS)
    return () => {
      alive = false
      window.clearInterval(t)
    }
  }, [path])
  return state
}

// Конструктор в режиме «расход»: стор Конструктора (зона canvas), если он
// есть в сборке, — сразу режим 'live'; плюс appStore.flowFocus с агентом.
const flowMods = import.meta.glob('../flow/store.ts', { eager: true }) as Record<
  string, { useFlowStore?: { getState: () => { setMode?: (m: string) => void } } }
>
const flowStore = Object.values(flowMods)[0]?.useFlowStore

function openCost(agentId: string): void {
  const app = useAppStore.getState()
  app.setFlowFocus({ mode: 'cost', agentId })
  flowStore?.getState().setMode?.('live')
  app.setSection('flow')
}

const scroll = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })

function Sidebar() {
  const profileLabel = useAppStore((s) => s.profileLabel)
  const profile = useAppStore((s) => s.profile)
  const online = useAppStore((s) => s.online)
  return (
    <SideMenu
      screen="account"
      sections={[{
        id: 'account',
        title: 'Кабинет',
        items: [
          { id: 'acc-me', label: 'аккаунт', meta: profileLabel || profile || undefined, onSelect: () => scroll('acc-me') },
          { id: 'acc-limits', label: 'лимиты и расход', onSelect: () => scroll('acc-limits') },
          { id: 'acc-spend', label: 'кто тратит сейчас', onSelect: () => scroll('acc-spend') },
          {
            id: 'acc-server', label: 'сервер', meta: online ? 'работает' : 'нет связи',
            metaTone: online ? 'dim' : 'rec', onSelect: () => scroll('acc-server'),
          },
        ],
      }]}
    />
  )
}

function fmtUptime(sec: number): string {
  const m = Math.floor(sec / 60)
  if (m < 60) return `${m} мин`
  const h = Math.floor(m / 60)
  if (h < 48) return `${h} ч ${m % 60} мин`
  return `${Math.floor(h / 24)} сут ${h % 24} ч`
}
const gb = (mb: number) => (mb / 1024).toFixed(1).replace('.', ',')
const num = (n: number) => String(n).replace('.', ',')

function Limits() {
  const limits = useLimits()
  return (
    <section id="acc-limits">
      <h2 className="abx-acc__h">Лимиты</h2>
      <div className="abx-acc__cols">
        {(limits?.providers ?? []).map((p) => {
          const left = leftPercent(p)
          const ok = isEnough(p)
          return (
            <div key={p.id} className="abx-acc__prov">
              <div className="abx-acc__prow">
                <span className="abx-acc__pname">{p.title}</span>
                {left !== null && p.available ? <span className="abx-acc__pct">осталось {left}%</span> : null}
              </div>
              {left === null || !p.available ? (
                <div className="abx-acc__muted">лимиты недоступны{p.reason ? `: ${p.reason}` : ''}</div>
              ) : (
                <>
                  {p.windows.map((w) => {
                    const wl = windowLeft(w)
                    return (
                      <div key={w.kind} className="abx-limits__win">
                        <div className="abx-acc__muted">
                          {windowTitle(w)} — осталось {wl}% · {w.resetsAt ? `сброс ${mskTime(w.resetsAt)}` : 'окно не начато'}
                        </div>
                        <div className="abx-limits__bar">
                          <div className={`abx-limits__fill ${!ok && w.kind === '5h' ? 'is-warn' : 'is-ok'}`} style={{ width: `${wl}%` }} />
                        </div>
                      </div>
                    )
                  })}
                  <div className="abx-acc__muted">{fmtRate(p.tokensPerMin)} ток/мин{fmtPctRate(p) ? ` · окно 5 ч тратится ${fmtPctRate(p)}` : ''}</div>
                  <div className={ok ? 'tone-ok' : 'tone-warn'}>{forecastText(p)}</div>
                </>
              )}
            </div>
          )
        })}
        {!limits ? <div className="abx-acc__muted">загрузка…</div> : null}
      </div>
    </section>
  )
}

function Spend() {
  const { data, error } = usePolled<ByAgent>('/usage/by-agent')
  const rows = (data?.rows ?? []).slice(0, 12)
  return (
    <section id="acc-spend">
      <h2 className="abx-acc__h">Кто тратит сейчас</h2>
      <div className="abx-acc__spend">
        {rows.map((r) => {
          const pct = Math.round(r.share * 100)
          const engine = r.engine?.replace(/^claude-/, '') ?? r.agentType ?? ''
          return (
            <button
              key={r.agentId}
              className="abx-acc__agent"
              onClick={() => openCost(r.agentId)}
              title={`${fmtRate(r.tokensPerMin)} ток/мин — открыть в Конструкторе, режим «расход»`}
            >
              <span className="abx-acc__aname">
                {r.title}
                {engine ? <span className="abx-acc__engine"> {engine}</span> : null}
              </span>
              <span className="abx-acc__share"><span style={{ width: `${Math.max(pct, r.tokensPerMin > 0 ? 2 : 0)}%` }} /></span>
              <span className="abx-acc__sharepct">{pct}%</span>
            </button>
          )
        })}
        {data && !rows.length ? <div className="abx-acc__muted">сейчас никто не тратит</div> : null}
        {error && !data ? <div className="abx-acc__muted">нет данных: {error}</div> : null}
      </div>
    </section>
  )
}

function Server() {
  const { data, error } = usePolled<ServerInfo>('/server')
  const online = useAppStore((s) => s.online)
  return (
    <section id="acc-server">
      <h2 className="abx-acc__h">
        Сервер <span className={`abx-acc__live${online && !error ? '' : ' is-off'}`} />
      </h2>
      {data ? (
        <div className="abx-acc__rows">
          <div className="abx-acc__row"><span>адрес</span><span className="abx-acc__mono">{data.address}</span></div>
          <div className="abx-acc__row">
            <span>живые сессии</span>
            <span>
              {data.ownLiveSessions} ваших · {data.liveSessions}
              {data.maxSessions > 0 ? ` из ${data.maxSessions}` : ''} на сервере
            </span>
          </div>
          <div className="abx-acc__row">
            <span>память и диск</span>
            <span>
              {gb(data.memory.availableMb)} из {gb(data.memory.totalMb)} ГБ свободно · диск {num(data.disk.freeGb)} из {num(data.disk.totalGb)} ГБ
            </span>
          </div>
          <div className="abx-acc__row">
            <span>аптайм</span>
            <span>
              панель {fmtUptime(data.uptimeSec.panel)} · система {fmtUptime(data.uptimeSec.system)}
              {data.restartKillsSessions ? <span className="abx-acc__warn"> · рестарт убивает сессии</span> : null}
            </span>
          </div>
        </div>
      ) : (
        <div className="abx-acc__muted">{error ? `нет данных: ${error}` : 'загрузка…'}</div>
      )}
    </section>
  )
}

function View() {
  const profileLabel = useAppStore((s) => s.profileLabel)
  const profile = useAppStore((s) => s.profile)
  const logout = useAppStore((s) => s.logout)
  const name = profileLabel || profile || 'оператор'
  return (
    <div className="abx-acc">
      <div className="abx-acc__page">
        <section id="acc-me" className="abx-acc__head">
          <span className="abx-acc__avatar">{name.slice(0, 1).toUpperCase()}</span>
          <span className="abx-acc__name">{name}</span>
          <span className="abx-acc__gap" />
          <button className="abx-acc__logout" onClick={() => void logout()}>выйти</button>
        </section>
        <Limits />
        <Spend />
        <Server />
      </div>
    </div>
  )
}

const account: SectionModule = { Sidebar, View }
export default account
